import { useCallback, useEffect, useRef, useState } from 'react';

export const useResizableColumns = ({
  defaultWidths,
  storageKey,
  minWidths = {}
}) => {
  const [colWidths, setColWidths] = useState(defaultWidths);
  const resizeStateRef = useRef(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return;
      setColWidths((prev) => ({
        ...prev,
        ...parsed
      }));
    } catch {
      // ignore localStorage errors
    }
  }, [storageKey]);

  const saveColWidths = useCallback((nextWidths) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(nextWidths));
    } catch {
      // ignore localStorage errors
    }
  }, [storageKey]);

  const resetColWidths = useCallback(() => {
    setColWidths(defaultWidths);
    saveColWidths(defaultWidths);
  }, [defaultWidths, saveColWidths]);

  const startResize = useCallback((columnKey, event) => {
    event.preventDefault();
    event.stopPropagation();
    resizeStateRef.current = {
      columnKey,
      startX: event.clientX,
      startWidth: Number(colWidths[columnKey]) || 0,
      minWidth: Number(minWidths[columnKey]) || 80
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [colWidths, minWidths]);

  useEffect(() => {
    const onMouseMove = (event) => {
      const state = resizeStateRef.current;
      if (!state) return;
      const delta = event.clientX - state.startX;
      const next = Math.max(state.minWidth, state.startWidth + delta);
      setColWidths((prev) => ({
        ...prev,
        [state.columnKey]: next
      }));
    };

    const onMouseUp = () => {
      if (!resizeStateRef.current) return;
      resizeStateRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setColWidths((prev) => {
        saveColWidths(prev);
        return prev;
      });
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [saveColWidths]);

  return {
    colWidths,
    setColWidths,
    startResize,
    saveColWidths,
    resetColWidths
  };
};

